import React, { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { createServerFn } from "@tanstack/react-start";
import type { StripeConnectInstance } from "@stripe/connect-js";
import { Button, buttonVariants } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { FormField } from "~/components/ui/form-field";
import { Eye, Plus, Settings } from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  type InvoiceStatus,
  createInvoiceForCoach,
  getInvoicesForCoach,
} from "~/lib/server/invoice";
import { getCoach } from "~/lib/server/coach";
import { brand } from "~/lib/ui";
import { Modal } from "~/components/ui/modal/modal";
import { cn } from "~/lib/cn";
import { clientEnv } from "~/lib/utils";
import { EmptyState } from "~/components/ui/empty-state";
import { serverEnv } from "~/lib/server-env";
import { requireCoachAuth } from "~/lib/coach-middleware";
import { STRIPE_API_VERSION } from "~/lib/stripe/version";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";

const PaymentDetailsPopupAsync = React.lazy(() =>
  import("~/components/stripe/payment-details-popup").then((mod) => ({
    default: mod.PaymentDetailsPopup,
  })),
);

const ConnectComponentsProviderAsync = React.lazy(() =>
  import("@stripe/react-connect-js").then((mod) => ({
    default: mod.ConnectComponentsProvider,
  })),
);

export const getInvoicesForCoachData = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const invoices = await getInvoicesForCoach({
      data: { coachId: context.coach.id },
    });

    return invoices.map((i) => ({
      id: i.id,
      customerName: i.customerName,
      customerEmail: i.customerEmail,
      status: i.status as InvoiceStatus,
      amount: i.amount,
      createdOn: i.createdAt ? new Date(i.createdAt) : null,
      paymentDate: i.paidOn ? new Date(i.paidOn) : null,
      currency: i.currency,
      stripeChargeId: i.stripeChargeId,
      url: i.url,
    }));
  });

export const createStripeAccountSession = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(z.object({ coachId: z.string() }))
  .handler(async ({ data, context: _context }) => {
    const coach = await getCoach(data.coachId);
    if (!coach) {
      throw new Error("Coach not found");
    }
    if (!coach.stripeAccountId) {
      throw new Error("Coach does not have a connected Stripe account");
    }

    const stripe = new (await import("stripe")).Stripe(
      serverEnv.STRIPE_STANDARD_SECRET_KEY!,
      { apiVersion: STRIPE_API_VERSION },
    );

    const session = await stripe.accountSessions.create({
      account: coach.stripeAccountId,
      components: {
        payment_details: {
          enabled: true,
        },
      },
    });

    return session.client_secret;
  });

const createInvoiceSchema = z.object({
  coachId: z.string(),
  customerName: z.string().trim().min(1),
  customerEmail: z.string().trim().email(),
  description: z.string().min(1),
  amount: z.number().min(0.01),
  currency: z.string().trim().min(1),
  dueDate: z.string().refine((date) => {
    const parsed = new Date(date);
    const tomorrow = new Date(Date.now() + 86400000);
    return parsed >= tomorrow;
  }, "Due date must be at least tomorrow"),
});

type InvoiceFormData = z.infer<typeof createInvoiceSchema>;

export const createInvoiceAction = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(createInvoiceSchema)
  .handler(async ({ data, context: _context }) => {
    const coach = await getCoach(data.coachId);
    if (!coach) {
      throw new Error("Coach not found");
    }

    await createInvoiceForCoach({
      data: {
        customerName: data.customerName,
        customerEmail: data.customerEmail,
        description: data.description,
        currency: data.currency,
        amount: Math.round(data.amount * 100),
        dueDate: new Date(data.dueDate),
      },
    });

    return { success: true };
  });

// Status Badge Component
export const StatusBadge = ({ status }: { status: InvoiceStatus }) => {
  const statusStyles: Record<InvoiceStatus, string> = {
    paid: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100",
    uncollectible: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
    void: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100",
    draft: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100",
    open: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100",
  };

  return (
    <Badge variant="secondary" className={statusStyles[status]}>
      {status}
    </Badge>
  );
};

const formatCurrency = (amount: number, currency: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amount / 100);
};

export const Route = createFileRoute("/coach/_backend/invoices/")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["coach-invoices"],
      queryFn: () => getInvoicesForCoachData(),
    });
    return {};
  },
  component: InvoicesPage,
});

function InvoicesPage() {
  const coach = useCoachPrivateProfileRequired();

  const { register, handleSubmit, formState, reset } = useForm<InvoiceFormData>(
    {
      resolver: zodResolver(createInvoiceSchema),
      defaultValues: {
        coachId: coach.id,
        customerName: "",
        customerEmail: "",
        description: "",
        currency: "USD",
        amount: 0,
        dueDate: "",
      },
    },
  );

  const { errors } = formState;
  const [createInvoiceVisible, setCreateInvoiceVisible] = useState(false);
  const [state, setState] = useState({ currentPage: 1 });
  const totalPages = 10;
  const [stripeConnectInstance, setStripeConnectInstance] =
    useState<StripeConnectInstance | null>(null);

  const queryClient = useQueryClient();

  const { data: invoices = [] } = useQuery({
    queryKey: ["coach-invoices"],
    queryFn: () => getInvoicesForCoachData(),
  });

  const createInvoiceMutation = useMutation({
    mutationFn: (data: InvoiceFormData) => createInvoiceAction({ data }),
    onSuccess: () => {
      toast.success("Invoice created successfully");
      queryClient.invalidateQueries({ queryKey: ["coach-invoices"] });
      setCreateInvoiceVisible(false);
      reset();
    },
    onError: () => {
      toast.error("Failed to create invoice");
    },
  });

  // Loading Stripe Connect is frontend-only
  useEffect(() => {
    const loadStripe = async () => {
      if (!coach.stripeAccountId) {
        return;
      }

      const { loadConnectAndInitialize } = await import("@stripe/connect-js");
      const instance = loadConnectAndInitialize({
        publishableKey: clientEnv.VITE_STRIPE_PUBLISHABLE_KEY!,
        fetchClientSecret: () =>
          createStripeAccountSession({ data: { coachId: coach.id } }),
        appearance: {
          variables: {
            colorPrimary: brand["700"],
            borderRadius: "9999px",
          },
        },
      });

      setStripeConnectInstance(instance);
    };

    loadStripe();
  }, [coach.stripeAccountId, coach.id]);

  const handlePageChange = (page: number) => {
    setState((prev) => ({ ...prev, currentPage: page }));
  };

  const canSendInvoices = coach.stripeChargesEnabled && coach.contractSigned;

  const onSubmit = (data: InvoiceFormData) => {
    createInvoiceMutation.mutate(data);
  };

  const renderContent = () => (
    <div className="container mx-auto flex flex-col gap-8 px-4 py-8">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="font-heading text-3xl">Invoicing</h1>
          <p className="text-text-secondary text-sm">
            Create and send invoices to your customers.
          </p>
        </div>
        <div className="grow-0">
          <Link to="/coach/stripe-connect">
            <Button variant="ghost" size="icon">
              <Settings />
            </Button>
          </Link>
        </div>
      </div>

      {!canSendInvoices && (
        <>
          <p>Please finish your payment setup to start sending invoices.</p>
          <Link to="/coach/stripe-connect">
            <Button>Go to Settings</Button>
          </Link>
        </>
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl">Invoices</h2>
        {canSendInvoices && (
          <Modal.Root
            open={createInvoiceVisible}
            onOpenChange={setCreateInvoiceVisible}
          >
            <Modal.Trigger
              className={cn(
                buttonVariants({
                  variant: "default",
                }),
              )}
              onClick={() => setCreateInvoiceVisible(true)}
            >
              <Plus className="mr-2" />
              Create Invoice
            </Modal.Trigger>
            <Modal.Panel>
              <Modal.Title>Create new Invoice</Modal.Title>
              <form
                className="mt-4 flex flex-col gap-4"
                onSubmit={handleSubmit(onSubmit)}
              >
                <input type="hidden" {...register("coachId")} />

                <FormField
                  label="Customer Name"
                  required
                  helpText="Customer's full name"
                  error={errors.customerName?.message}
                >
                  <Input
                    type="text"
                    {...register("customerName")}
                    error={!!errors.customerName}
                  />
                </FormField>

                <FormField
                  label="Email"
                  required
                  helpText="Customer's email address"
                  error={errors.customerEmail?.message}
                >
                  <Input
                    type="email"
                    {...register("customerEmail")}
                    error={!!errors.customerEmail}
                  />
                </FormField>

                <FormField
                  label="Description"
                  required
                  helpText="Description of the invoice"
                  error={errors.description?.message}
                >
                  <Input
                    type="text"
                    {...register("description")}
                    error={!!errors.description}
                  />
                </FormField>

                <FormField
                  label="Amount"
                  required
                  helpText="Amount in USD"
                  error={errors.amount?.message}
                >
                  <Input
                    type="number"
                    step="0.01"
                    {...register("amount", { valueAsNumber: true })}
                    error={!!errors.amount}
                  />
                </FormField>

                <FormField
                  label="Currency"
                  required
                  error={errors.currency?.message}
                >
                  <select
                    {...register("currency")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="JPY">JPY</option>
                    <option value="CHF">CHF</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                    <option value="CNY">CNY</option>
                    <option value="INR">INR</option>
                    <option value="NZD">NZD</option>
                    <option value="NOK">NOK</option>
                  </select>
                </FormField>

                <FormField
                  label="Due Date"
                  required
                  helpText="Date when payment is due"
                  error={errors.dueDate?.message}
                >
                  <Input
                    type="date"
                    {...register("dueDate")}
                    error={!!errors.dueDate}
                  />
                </FormField>

                <Button
                  type="submit"
                  disabled={createInvoiceMutation.isPending}
                >
                  {createInvoiceMutation.isPending
                    ? "Creating..."
                    : "Create Invoice"}
                </Button>
              </form>
            </Modal.Panel>
          </Modal.Root>
        )}
      </div>

      {0 === invoices.length ? (
        <EmptyState
          title="No Invoices Yet"
          description="You don't have any invoices here. Start creating your first invoice to keep track of your transactions and stay organized!"
          className="min-h-[50vh]"
        >
          <svg
            width="136"
            height="109"
            viewBox="0 0 136 109"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M79.6136 40.1548C71.2336 40.2048 62.7236 39.8148 54.7636 37.6048C46.8036 35.3948 39.7636 31.2248 33.3636 26.3548C29.1736 23.1848 25.3636 20.6648 19.9236 21.0448C14.6046 21.3244 9.51699 23.309 5.41362 26.7048C-1.50638 32.7648 -0.456382 43.9648 2.30362 51.8448C6.46362 63.7148 19.1236 71.9548 29.8736 77.3248C42.3036 83.5348 55.9536 87.1448 69.6436 89.2148C81.6436 91.0348 97.0536 92.3648 107.444 84.5248C116.994 77.3348 119.614 60.8948 117.274 49.7948C116.704 46.5163 114.958 43.5581 112.364 41.4748C105.654 36.5748 95.6536 39.8448 88.1136 40.0148C85.3636 40.0748 82.4736 40.1448 79.6136 40.1548Z"
              fill="#EFE6F6"
            />
            <path
              d="M34.2734 13.0244V5.64473"
              stroke="#D1C6DA"
              strokeWidth="0.85"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M36.6035 3.49512H40.9035"
              stroke="#D1C6DA"
              strokeWidth="0.85"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M120.334 87.0547V91.3547"
              stroke="#D1C6DA"
              strokeWidth="0.85"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M118.184 89.2051H122.484"
              stroke="#D1C6DA"
              strokeWidth="0.85"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.2632 68.4844C6.80444 68.4844 7.2432 68.0456 7.2432 67.5044C7.2432 66.9632 6.80444 66.5244 6.2632 66.5244C5.72196 66.5244 5.2832 66.9632 5.2832 67.5044C5.2832 68.0456 5.72196 68.4844 6.2632 68.4844Z"
              fill="#D1C6DA"
            />
            <path
              d="M89.7036 5.78422C90.2449 5.78422 90.6836 5.34546 90.6836 4.80422C90.6836 4.26298 90.2449 3.82422 89.7036 3.82422C89.1624 3.82422 88.7236 4.26298 88.7236 4.80422C88.7236 5.34546 89.1624 5.78422 89.7036 5.78422Z"
              fill="#D1C6DA"
            />
            <path
              d="M62.5438 108.655C82.8126 108.655 99.2437 107.634 99.2437 106.375C99.2437 105.116 82.8126 104.095 62.5438 104.095C42.2749 104.095 25.8438 105.116 25.8438 106.375C25.8438 107.634 42.2749 108.655 62.5438 108.655Z"
              fill="#EFE6F6"
            />
            <path
              d="M34.2734 13.0244H87.5734C88.8994 13.0244 90.1712 13.5512 91.1089 14.4889C92.0466 15.4266 92.5734 16.6983 92.5734 18.0244V81.4344C92.5734 82.7605 92.0466 84.0323 91.1089 84.97C90.1712 85.9076 88.8994 86.4344 87.5734 86.4344H27.4434C26.1173 86.4344 24.8455 85.9076 23.9078 84.97C22.9701 84.0323 22.4434 82.7605 22.4434 81.4344V24.9744L34.2734 13.0244Z"
              fill="white"
              stroke="#D1C6DA"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M92.333 43.9248H37.373V87.0448H92.333V43.9248Z"
              fill="#D1C6DA"
            />
            <path
              d="M22.4434 24.9744H32.1334C32.7018 24.9718 33.2461 24.7441 33.6472 24.3412C34.0482 23.9383 34.2734 23.3929 34.2734 22.8244V13.0244L22.4434 24.9744Z"
              fill="white"
              stroke="#D1C6DA"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M92.063 30.1543H37.703C35.5878 30.1543 33.873 31.869 33.873 33.9843V45.6143C33.873 47.7295 35.5878 49.4443 37.703 49.4443H92.063C94.1783 49.4443 95.893 47.7295 95.893 45.6143V33.9843C95.893 31.869 94.1783 30.1543 92.063 30.1543Z"
              fill="white"
              stroke="#D1C6DA"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M44.4338 43.1644C46.2784 43.1644 47.7738 41.669 47.7738 39.8244C47.7738 37.9797 46.2784 36.4844 44.4338 36.4844C42.5891 36.4844 41.0938 37.9797 41.0938 39.8244C41.0938 41.669 42.5891 43.1644 44.4338 43.1644Z"
              fill="#D1C6DA"
            />
            <path
              d="M54.9337 43.1644C56.7784 43.1644 58.2738 41.669 58.2738 39.8244C58.2738 37.9797 56.7784 36.4844 54.9337 36.4844C53.0891 36.4844 51.5938 37.9797 51.5938 39.8244C51.5938 41.669 53.0891 43.1644 54.9337 43.1644Z"
              fill="#D1C6DA"
            />
            <path
              d="M65.424 43.1644C67.2686 43.1644 68.764 41.669 68.764 39.8244C68.764 37.9797 67.2686 36.4844 65.424 36.4844C63.5793 36.4844 62.084 37.9797 62.084 39.8244C62.084 41.669 63.5793 43.1644 65.424 43.1644Z"
              fill="#D1C6DA"
            />
            <path
              d="M92.063 51.5352H37.703C35.5878 51.5352 33.873 53.2499 33.873 55.3652V66.9952C33.873 69.1104 35.5878 70.8252 37.703 70.8252H92.063C94.1783 70.8252 95.893 69.1104 95.893 66.9952V55.3652C95.893 53.2499 94.1783 51.5352 92.063 51.5352Z"
              fill="white"
              stroke="#D1C6DA"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M44.4338 64.555C46.2784 64.555 47.7738 63.0596 47.7738 61.215C47.7738 59.3704 46.2784 57.875 44.4338 57.875C42.5891 57.875 41.0938 59.3704 41.0938 61.215C41.0938 63.0596 42.5891 64.555 44.4338 64.555Z"
              fill="#D1C6DA"
            />
            <path
              d="M54.9337 64.555C56.7784 64.555 58.2738 63.0596 58.2738 61.215C58.2738 59.3704 56.7784 57.875 54.9337 57.875C53.0891 57.875 51.5938 59.3704 51.5938 61.215C51.5938 63.0596 53.0891 64.555 54.9337 64.555Z"
              fill="#D1C6DA"
            />
            <path
              d="M65.424 64.555C67.2686 64.555 68.764 63.0596 68.764 61.215C68.764 59.3704 67.2686 57.875 65.424 57.875C63.5793 57.875 62.084 59.3704 62.084 61.215C62.084 63.0596 63.5793 64.555 65.424 64.555Z"
              fill="#D1C6DA"
            />
            <path
              d="M92.063 72.9248H37.703C35.5878 72.9248 33.873 74.6396 33.873 76.7548V88.3848C33.873 90.5001 35.5878 92.2148 37.703 92.2148H92.063C94.1783 92.2148 95.893 90.5001 95.893 88.3848V76.7548C95.893 74.6396 94.1783 72.9248 92.063 72.9248Z"
              fill="white"
              stroke="#D1C6DA"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M44.4338 85.9349C46.2784 85.9349 47.7738 84.4395 47.7738 82.5949C47.7738 80.7503 46.2784 79.2549 44.4338 79.2549C42.5891 79.2549 41.0938 80.7503 41.0938 82.5949C41.0938 84.4395 42.5891 85.9349 44.4338 85.9349Z"
              fill="#D1C6DA"
            />
            <path
              d="M54.9337 85.9349C56.7784 85.9349 58.2738 84.4395 58.2738 82.5949C58.2738 80.7503 56.7784 79.2549 54.9337 79.2549C53.0891 79.2549 51.5938 80.7503 51.5938 82.5949C51.5938 84.4395 53.0891 85.9349 54.9337 85.9349Z"
              fill="#D1C6DA"
            />
            <path
              d="M65.424 85.9349C67.2686 85.9349 68.764 84.4395 68.764 82.5949C68.764 80.7503 67.2686 79.2549 65.424 79.2549C63.5793 79.2549 62.084 80.7503 62.084 82.5949C62.084 84.4395 63.5793 85.9349 65.424 85.9349Z"
              fill="#D1C6DA"
            />
            <path
              d="M96.9839 54.5147C108.433 54.5147 117.714 45.2335 117.714 33.7847C117.714 22.3358 108.433 13.0547 96.9839 13.0547C85.535 13.0547 76.2539 22.3358 76.2539 33.7847C76.2539 45.2335 85.535 54.5147 96.9839 54.5147Z"
              fill="white"
              stroke="#D1C6DA"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M111.703 48.8242L117.223 54.3442"
              stroke="#D1C6DA"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M116.607 51.6652L114.893 53.4578C114.122 54.2642 114.151 55.5429 114.957 56.3138L129.848 70.5486C130.654 71.3195 131.933 71.2907 132.704 70.4843L134.418 68.6916C135.188 67.8852 135.16 66.6065 134.353 65.8356L119.463 51.6009C118.656 50.83 117.378 50.8587 116.607 51.6652Z"
              fill="white"
              stroke="#D1C6DA"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {canSendInvoices && (
            <Button onClick={() => setCreateInvoiceVisible(true)}>
              Create Invoice
            </Button>
          )}
        </EmptyState>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Created On</TableHead>
                <TableHead>Paid On</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    {invoice.url && (
                      <a
                        href={invoice.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button size="icon" variant="ghost">
                          <Eye />
                        </Button>
                      </a>
                    )}
                  </TableCell>
                  <TableCell>
                    <p>{invoice.customerName}</p>
                    <p className="text-text-tertiary text-xs">
                      {invoice.customerEmail}
                    </p>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={invoice.status} />
                  </TableCell>
                  <TableCell className="flex items-center gap-2">
                    {formatCurrency(invoice.amount, invoice.currency)}
                    {invoice.stripeChargeId && (
                      <React.Suspense fallback={null}>
                        <PaymentDetailsPopupAsync
                          paymentId={invoice.stripeChargeId}
                        />
                      </React.Suspense>
                    )}
                  </TableCell>
                  <TableCell>
                    {invoice.createdOn &&
                      Intl.DateTimeFormat(undefined).format(invoice.createdOn)}
                  </TableCell>
                  <TableCell>
                    {invoice.paymentDate &&
                      Intl.DateTimeFormat(undefined).format(
                        invoice.paymentDate,
                      )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="hidden items-center justify-between space-x-2 py-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(state.currentPage - 1)}
              disabled={1 === state.currentPage}
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </Button>
            <div className="flex items-center space-x-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <Button
                    key={page}
                    variant={state.currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Button>
                ),
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(state.currentPage + 1)}
              disabled={state.currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return stripeConnectInstance ? (
    <React.Suspense fallback={renderContent()}>
      <ConnectComponentsProviderAsync connectInstance={stripeConnectInstance}>
        {renderContent()}
      </ConnectComponentsProviderAsync>
    </React.Suspense>
  ) : (
    renderContent()
  );
}
